import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CalculatedContractStatusEnum,
  FirmwareAvailabilityStatusEnum,
  ServiceContractTypes,
} from '@uptime-bff/api-contract';
import type { RootState } from 'core/store';
import { countSecondaryStatusTags, lengthOf } from 'feature/assets/utils';
import { DEFAULT_MAP_VARIANT, MapVariant } from 'feature/map/types/map-variant';
export interface MapFiltersType {
  models?: string[];
  companyCountries?: string[];
  diamGroupId?: string[];
  secondaryStatus?: string[];
  operationalStatus?: string[];
  hfsServiceContractType?: ServiceContractTypes[];
  firmwareAvailabilityStatus?: FirmwareAvailabilityStatusEnum[];
  servicesExpiryStatus?: CalculatedContractStatusEnum[];
}

interface MapState {
  filters: MapFiltersType;
  variant?: MapVariant;
}

const initialState: MapState = {
  filters: {},
};

const setOrClear = <T>(values: T[]): T[] | undefined => (values.length > 0 ? values : undefined);

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setMapFilterByModels: (state, action: PayloadAction<string[]>) => {
      state.filters.models = setOrClear(action.payload);
    },
    setMapFilterByCompanies: (state, action: PayloadAction<string[]>) => {
      state.filters.diamGroupId = setOrClear(action.payload);
    },
    setMapFilterByCompanyCountries: (state, action: PayloadAction<string[]>) => {
      state.filters.companyCountries = setOrClear(action.payload);
    },
    setMapFilterBySecondaryStatus: (state, action: PayloadAction<string[]>) => {
      state.filters.secondaryStatus = setOrClear(action.payload);
    },
    setMapFilterByOperationalStatus: (state, action: PayloadAction<string[]>) => {
      state.filters.operationalStatus = setOrClear(action.payload);
    },
    setMapFilterByFirmwareAvailabilityStatus: (
      state,
      action: PayloadAction<FirmwareAvailabilityStatusEnum[]>
    ) => {
      state.filters.firmwareAvailabilityStatus = setOrClear(action.payload);
    },
    setMapFilterByServicesExpiryStatus: (
      state,
      action: PayloadAction<CalculatedContractStatusEnum[]>
    ) => {
      state.filters.servicesExpiryStatus = setOrClear(action.payload);
    },
    setMapFilterByHfsServiceContractType: (state, action: PayloadAction<ServiceContractTypes[]>) => {
      if (action.payload.includes(ServiceContractTypes.WithoutServices)) {
        state.filters.hfsServiceContractType = [ServiceContractTypes.WithoutServices];
        return;
      }
      state.filters.hfsServiceContractType = setOrClear(action.payload);
    },
    clearMapFilters: (state) => {
      state.filters = {};
    },
    setMapVariant: (state, action: PayloadAction<MapVariant>) => {
      state.variant = action.payload;
    },  },
});

export const {
  setMapFilterByModels,
  setMapFilterByCompanies,
  setMapFilterByCompanyCountries,
  setMapFilterBySecondaryStatus,
  setMapFilterByOperationalStatus,
  setMapFilterByFirmwareAvailabilityStatus,
  setMapFilterByServicesExpiryStatus,
  setMapFilterByHfsServiceContractType,
  clearMapFilters,
  setMapVariant,
} = mapSlice.actions;
export default mapSlice.reducer;

export const selectMapFilters = (state: RootState) => state.map.filters;

export const selectMapVariant = (state: RootState) => state.map.variant ?? DEFAULT_MAP_VARIANT;
export const selectActiveMapFiltersCount = createSelector([selectMapFilters], (filters) =>
  lengthOf(filters.operationalStatus)
  + countSecondaryStatusTags(filters.secondaryStatus)
  + lengthOf(filters.firmwareAvailabilityStatus)
  + lengthOf(filters.servicesExpiryStatus)
  + lengthOf(filters.hfsServiceContractType)
  + lengthOf(filters.models)
  + lengthOf(filters.diamGroupId)
  + lengthOf(filters.companyCountries));
